/**
 * Gaussian Splatting 多引擎架构 - 统一类型定义
 *
 * 此文件定义多引擎共享的类型，与具体渲染引擎无关。
 */

// ==================== lod-meta.json 相关类型 ====================

/** lod-meta.json 中的包围盒格式 */
export interface LODMetaBound {
  /** 包围盒最小角点 [x, y, z] */
  min: [number, number, number]
  /** 包围盒最大角点 [x, y, z] */
  max: [number, number, number]
}

/** LOD 数据引用 */
export interface LODDataRef {
  /** filenames 数组索引 */
  file: number
  /** 数据偏移 */
  offset: number
  /** 高斯点数量 */
  count: number
}

/** lod-meta.json 树节点 */
export interface LODMetaTreeNode {
  /** 节点包围盒（本地坐标） */
  bound: LODMetaBound
  /** 子节点数组（内部节点） */
  children?: LODMetaTreeNode[]
  /** LOD 数据引用（叶子节点，当前仅支持 "0"） */
  lods?: Record<string, LODDataRef>
}

/** lod-meta.json 根结构 */
export interface LODMetaData {
  /** 资产内禀世界偏移，未提供时默认为 [0, 0, 0] */
  offset?: [number, number, number]
  /** LOD 层级总数 */
  lodLevels: number
  /** bundle 内子块 WebP 文件路径列表 */
  filenames: string[]
  /** 八叉树根节点 */
  tree: LODMetaTreeNode
}

// LOD 纯类型从 shared 层 re-export
import type { LODConfig } from '@/shared/types/gsplat'

export type {
  GSplatAutoDeviceBudgetRecommendations,
  GSplatDeviceBudgetAppliedFields,
  GSplatDeviceBudgetBrowser,
  GSplatDeviceBudgetClampReason,
  GSplatDeviceBudgetConfig,
  GSplatDeviceBudgetDeviceInfo,
  GSplatDeviceBudgetDiagnostics,
  GSplatDeviceBudgetGpuVendor,
  GSplatDeviceBudgetMode,
  GSplatDeviceBudgetOS,
  GSplatDeviceBudgetSource,
  GSplatFirstOrderUploadMode,
  GSplatInteractiveSortSchedulerConfig,
  GSplatInteractiveSortSchedulerMode,
  GSplatOrderUploadMode,
  LODConfig,
  LODEventData,
  LODEventType,
  LODLevelStat,
  LODStats,
} from '@/shared/types/gsplat'

// ==================== 引擎类型 ====================

/**
 * 支持的渲染引擎类型
 */
export type EngineType = 'playcanvas'

// ==================== 管理器配置 ====================

/**
 * 创建 GS3DManager 的配置
 */
export interface GS3DManagerConfig {
  /**
   * 渲染引擎类型
   */
  engine: EngineType

  /**
   * 场景对象（引擎特定）
   *
   * - playcanvas: pc.Application
   */
  scene: unknown

  /**
   * 渲染器对象（引擎特定，可选）
   *
   * - playcanvas: 通常不需要
   */
  renderer?: unknown

  /**
   * LOD 配置（可选）
   */
  lodConfig?: Partial<LODConfig>

  /** 是否携带跨域 Cookie（用于 CloudFront 签名 Cookie 等场景） */
  withCredentials?: boolean
}

// Manager 接口类型从 shared 层 re-export
export type {
  BoundingBox,
  IGS3DManager,
  LoadOptions,
  LoadResult,
  ScalarFilterOptions,
  ScalarFilterType,
  ScalarHistogramOptions,
  ScalarHistogramResult,
} from '@/shared/types/gsplat'

// ==================== 默认配置 ====================

/** 节点状态枚举 */
export const LODNodeState = {
  /** 未加载，需要网络请求 */
  Unloaded: 'unloaded',
  /** 已在加载队列中等待 */
  Queued: 'queued',
  /** 正在网络加载 */
  Loading: 'loading',
  /** 已加载，在内存中且被管理 */
  Loaded: 'loaded',
  /** 加载失败 */
  Failed: 'failed',
} as const

/** 默认 LOD 配置 */
export const DEFAULT_LOD_CONFIG: LODConfig = {
  loadSHN: true,
  maxCachePoints: 7_000_000,
  maxConcurrentLoads: 4,
  minScreenPixelSize: 8,
  screenSpaceErrorThreshold: 1.0,
  meshPoolInitialSize: 10,
  cacheHysteresis: 1.2,
  nodeProtectionTime: 2000,
  enableFrustumCulling: true,
  enableScreenSizeCulling: true,
  enableDistanceCulling: true,
  maxRenderDistance: Infinity,
  lodBaseDistance: 5,
  lodMultiplier: 3,
  lodUpdateDistance: 1,
  lodUpdateAngle: 0,
  lodBehindPenalty: 1,
  lodRangeMin: 0,
  lodRangeMax: 10,
  lodUnderfillLimit: 0,
  lodRefinementMode: 'step',
  maxLoadsPerFrame: 0,
  radialSorting: true,
  sortVisibilityCompact: false,
  sortVisibilityBehindMargin: 0.75,
  sortVisibilityRotationRefreshAngle: 12,
  worldStateUpdateInterval: 0,
  orderUploadMode: 'full',
  maxImmediateOrderUploadBytes: 2 * 1024 * 1024,
  maxOrderUploadBytesPerFrame: 2 * 1024 * 1024,
  maxOrderUploadChunksPerFrame: 1,
  minOrderUploadIntervalMs: 250,
  maxOrderUploadDelayMs: 1000,
  firstOrderUploadMode: 'full',
  allowFirstOrderFallback: true,
  allowOrderUploadFullFallback: true,
}
